// ============================================================================
// PairsForm — Surbrillance de menu : LE match le plus spécifique, tout menu
// ============================================================================
// Bug systématique (recette FX 2026-08-29) : « Mon profil » (/espace-perso)
// restait allumé sur /espace-perso/e-learning ou /espace-perso/outils-codev.
// L'ancienne garde ne comparait un item qu'à ses FRÈRES de section, alors que
// l'item plus spécifique vivait dans une AUTRE section (« Ma formation »).
//
// Règle unique : parmi TOUS les hrefs du menu (items + sections à lien
// direct), un seul est actif — le préfixe LE PLUS LONG du pathname courant.
// Partagée par la Sidebar desktop et la MobileNavigation (P6).
//
// Lot 10.8 (N263) : une entrée qui porte un `?tab=` (« Lettres de mission »
// → /o/:slug/animateurs?tab=lettres-mission) ne s'allume QUE sur cet onglet,
// et y bat l'entrée sans onglet du même chemin (« Animateurs ») ; sur un autre
// onglet, c'est l'entrée sans onglet qui s'allume. Les autres paramètres
// (ex. `?vitrine=codev`) ne jouent pas.

import type { MenuSection } from "./types";

/** Clé de comparaison d'un href de menu : pathname, plus `?tab=X` quand
 *  l'entrée en porte un — tout autre paramètre de query est ignoré. */
function cleHref(href: string): string {
  const [pathname, query = ""] = href.split("?");
  const tab = new URLSearchParams(query).get("tab");
  return tab ? `${pathname}?tab=${tab}` : pathname;
}

function decomposer(cle: string): { chemin: string; tab: string | null } {
  const [chemin, query = ""] = cle.split("?");
  return { chemin, tab: new URLSearchParams(query).get("tab") };
}

/** Tous les hrefs adressables du menu (items non désactivés + sections à
 *  lien direct), normalisés (`cleHref`) et dédupliqués. */
export function collecterHrefsMenu(sections: MenuSection[]): string[] {
  const set = new Set<string>();
  for (const s of sections) {
    if (s.href) set.add(cleHref(s.href));
    for (const i of s.items ?? []) {
      if (!i.disabled && i.href !== "#") set.add(cleHref(i.href));
    }
  }
  return [...set];
}

/** Le href actif : préfixe le plus long du pathname parmi ceux du menu — à
 *  chemin égal, l'entrée dont le `?tab=` est l'onglet courant (`search`) bat
 *  l'entrée sans onglet ; une entrée à onglet ne matche jamais hors de lui.
 *  Null si rien ne matche — plus de repli `/` → `/dashboard` (N254, lot 9.5) :
 *  `/dashboard` est une URL héritée que le menu ne porte plus, et `/` est la
 *  landing publique, sans menu. */
export function hrefActif(pathname: string, hrefs: string[], search = ""): string | null {
  const tabCourant = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("tab");
  let best: string | null = null;
  let bestChemin = "";
  let bestAOnglet = false;
  for (const h of hrefs) {
    if (h === "#" || !h) continue;
    const { chemin, tab } = decomposer(h);
    if (!(pathname === chemin || pathname.startsWith(chemin + "/"))) continue;
    if (tab && tab !== tabCourant) continue;
    const plusSpecifique =
      !best || chemin.length > bestChemin.length || (chemin.length === bestChemin.length && !!tab && !bestAOnglet);
    if (plusSpecifique) {
      best = h;
      bestChemin = chemin;
      bestAOnglet = !!tab;
    }
  }
  return best;
}

/** L'item/section `href` est-il LE match actif du menu ? */
export function estHrefActif(href: string, pathname: string, hrefs: string[], search = ""): boolean {
  const h = cleHref(href);
  if (h === "#") return false;
  return hrefActif(pathname, hrefs, search) === h;
}
